import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { FaRegHeart, FaUser, FaBars } from "react-icons/fa";
import { FiSearch } from "react-icons/fi";
import "./HomeHeader.css";
import { colors } from "../../../constants/colors";
import { clearStates } from "../../../constants/globalFunction";
import BrandLogo from "../../BrandLogo";
import Drawer from "./drawer";
import { FiShoppingCart } from "react-icons/fi";
import CartDrawer from "../../cart/CartDrawer";

const HomeHeader = ({ fetchDetails }) => {
  const navigate = useNavigate();
  const location = useLocation();
  // const isHeroOverlayPage =
  //   location.pathname === "/" || location.pathname.startsWith("/category");

  const isHeroOverlayPage = false;
  /** PDP-style pages: no standalone search chip (browse categories/menu instead). */
  const hideHeaderSearch =
    location.pathname === "/product" ||
    location.pathname.startsWith("/product/");

  const { cartCount, whishlistCount } = useSelector(
    (state) => state.countState
  );
  const { categories } = useSelector((state) => state.categoryState);
  const newMessageFlag = useSelector(
    (state) => state.supportTicketState.newMessage
  );
  const userData = useSelector((state) => state.userState.userData);

  const [isAccountDropdownOpen, setIsAccountDropdownOpen] = useState(false);
  const [isCategoriesDropdownOpen, setIsCategoriesDropdownOpen] =
    useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isCartDrawerOpen, setIsCartDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isScrolled, setIsScrolled] = useState(!isHeroOverlayPage);
  const [isHeaderHovered, setIsHeaderHovered] = useState(false);
  const accountDropdownRef = useRef(null);
  const categoriesDropdownRef = useRef(null);
  const drawerRef = useRef(null);
  const dropdownTimeoutRef = useRef(null);
  const observerRef = useRef(null);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  useEffect(() => {
    if (!isHeroOverlayPage) {
      setIsScrolled(true);
      return undefined;
    }
    setIsScrolled(false);
    observerRef.current?.disconnect();
    observerRef.current = null;

    const onScroll = () => setIsScrolled(window.scrollY > 10);

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isHeroOverlayPage, location.pathname]);

  useEffect(() => {
    setIsHeaderHovered(false);
  }, [location.pathname]);

  const iconColor =
    isHeroOverlayPage && !isScrolled && !isHeaderHovered
      ? colors.white
      : colors.primary;

  const headerClassName = [
    "homeHeader",
    isHeroOverlayPage ? "homeHeader--hero" : "homeHeader--page",
    isHeroOverlayPage &&
    (isScrolled || isHeaderHovered
      ? "homeHeader--scrolled"
      : "homeHeader--transparent"),
  ]
    .filter(Boolean)
    .join(" ");

  const toggleAccountDropdown = () => {
    setIsAccountDropdownOpen((prev) => !prev);
    setIsCategoriesDropdownOpen(false);
  };

  const openCategoriesDropdown = () => {
    // Clear any existing timeout
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
      dropdownTimeoutRef.current = null;
    }
    setIsCategoriesDropdownOpen(true);
    setIsAccountDropdownOpen(false);
  };

  const closeCategoriesDropdown = () => {
    // Add a small delay to prevent accidental closing
    dropdownTimeoutRef.current = setTimeout(() => {
      setIsCategoriesDropdownOpen(false);
    }, 100);
  };

  const toggleCategoriesDropdown = () => {
    setIsCategoriesDropdownOpen((prev) => !prev);
    setIsAccountDropdownOpen(false);
  };

  const toggleDrawer = () => {
    setIsDrawerOpen((prev) => !prev);
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
  };

  const openCartDrawer = () => {
    setIsCartDrawerOpen(true);
  };

  const closeCartDrawer = () => {
    setIsCartDrawerOpen(false);
    if (fetchDetails) {
      fetchDetails();
    }
    // Dispatch custom event to notify other components (like ProductDetails)
    window.dispatchEvent(new CustomEvent('cartUpdated'));
  };

  useEffect(() => {
    function handleClickOutside(event) {
      const clickedOutsideAccount =
        accountDropdownRef.current &&
        !accountDropdownRef.current.contains(event.target);
      const clickedOutsideDrawer =
        drawerRef.current && !drawerRef.current.contains(event.target);

      if (clickedOutsideAccount) {
        setIsAccountDropdownOpen(false);
      }

      if (isDrawerOpen && clickedOutsideDrawer) {
        closeDrawer();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      // Cleanup timeout on unmount
      if (dropdownTimeoutRef.current) {
        clearTimeout(dropdownTimeoutRef.current);
      }
    };
  }, [isDrawerOpen]);

  const onLogout = async () => {
    try {
      await clearStates();

      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const handleNavigation = (path, state = null) => {
    if (state) {
      navigate(path, state);
    } else {
      navigate(path);
    }
    closeDrawer();
  };

  // Get first 2 categories for desktop navigation
  const desktopCategories = categories?.slice(0, 2) || [];

  return (
    <header
      className={headerClassName}
      onMouseEnter={() => isHeroOverlayPage && setIsHeaderHovered(true)}
      onMouseLeave={() => setIsHeaderHovered(false)}
    >
      {/* Mobile Hamburger Menu */}
      {isMobile && (
        <button
          className="homeHeader__hamburger"
          onClick={toggleDrawer}
          aria-label="Menu"
        >
          <FaBars size={20} color={iconColor} />
        </button>
      )}

      {/* Logo - Centered on mobile, left on desktop */}
      <div
        className={`homeHeader__brand ${isMobile ? "homeHeader__brand--mobile" : ""
          }`}
        onClick={() => navigate("/")}
      >

        <BrandLogo
          className="homeHeader__logo"
          objectPosition="left center"
          alt="Yug Mantra — Design by Siddharth Fashion"
          isScrolled={isScrolled}
          isHeaderHovered={isHeaderHovered}
          isStaticPage={!isHeroOverlayPage}
        />


      </div>


      {/* Desktop Navigation Links */}
      {!isMobile && (
        <nav className="homeHeader__navigation">
          <button className="homeHeader__navLink" onClick={() => navigate("/")}>
            Home
          </button>

          <div
            className="homeHeader__navDropdown"
            ref={categoriesDropdownRef}
            onMouseEnter={openCategoriesDropdown}
            onMouseLeave={closeCategoriesDropdown}
          >
            <button
              className="homeHeader__navLink homeHeader__navLink--dropdown"
              onClick={() => navigate("/search")}
            >
              All Blouse
            </button>
            <div
              className={`homeHeader__navDropdownMenu ${isCategoriesDropdownOpen ? "show" : ""
                }`}
              onMouseEnter={() => {
                if (dropdownTimeoutRef.current) {
                  clearTimeout(dropdownTimeoutRef.current);
                  dropdownTimeoutRef.current = null;
                }
              }}
            >
              {categories?.map((cat) => (
                <button
                  key={cat?._id}
                  className="homeHeader__navDropdownItem"
                  onClick={() => {
                    navigate(`/category?category_id=${cat?._id}`, {
                      state: { category: cat },
                    });
                    setIsCategoriesDropdownOpen(false);
                  }}
                >
                  {cat?.name}
                </button>
              ))}
            </div>
          </div>

          {desktopCategories.map((cat) => (
            <button
              key={cat?._id}
              className="homeHeader__navLink"
              onClick={() =>
                navigate(`/category?category_id=${cat?._id}`, { state: { category: cat } })
              }
            >
              {cat?.name}
            </button>
          ))}
        </nav>
      )}

      {/* Right Side Actions */}
      <div className="homeHeader__actions">
        {!hideHeaderSearch && (
          <button
            type="button"
            className="homeHeader__iconBtn"
            aria-label="Search"
            onClick={() => navigate("/search")}
          >
            <FiSearch size={18} color={iconColor} />
          </button>
        )}

        <button
          type="button"
          className="homeHeader__iconBtn homeHeader__badgeWrapper"
          aria-label="Wishlist"
          onClick={() => navigate("/whishlist")}
        >
          <FaRegHeart size={18} color={iconColor} />
          {
            userData && (
              <span className="homeHeader__badge">{whishlistCount}</span>
            )
          }
        </button>

        <button
          type="button"
          className="homeHeader__iconBtn homeHeader__badgeWrapper"
          aria-label="Cart"
          onClick={openCartDrawer}
        >
          <FiShoppingCart size={18} color={iconColor} />
          {
            userData && (
              <span className="homeHeader__badge">{cartCount}</span>
            )
          }

        </button>
        {!isMobile && (
          <div className="homeHeader__account" ref={accountDropdownRef}>
            <button
              type="button"
              className="homeHeader__iconBtn"
              onClick={toggleAccountDropdown}
              aria-haspopup="menu"
              aria-expanded={isAccountDropdownOpen}
              aria-label="My Account"
            >
              <FaUser size={18} color={iconColor} />
            </button>
            {isAccountDropdownOpen && (
              <div className="homeHeader__dropdown" role="menu">
                {userData && (
                  <>
                    <button
                      className="homeHeader__dropdownItem"
                      onClick={() => {
                        navigate("/profile");
                      }}
                    >
                      Profile
                    </button>
                    <button
                      className="homeHeader__dropdownItem"
                      onClick={() => {
                        navigate("/change-password");
                      }}
                    >
                      Change Password
                    </button>
                  </>
                )}

                <button
                  className="homeHeader__dropdownItem"
                  onClick={() => {
                    navigate("/orders");
                  }}
                >
                  My Order
                </button>
                {userData && (
                  <>
                    <div className="homeHeader__dropdownItem" style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                      <button
                        onClick={() => navigate("/support-tickets")}
                        style={{ background: 'none', border: 'none', padding: 0 }}
                      >
                        My Tickets
                      </button>
                      {Number(newMessageFlag) === 1 && (
                        <span className="homeHeader__newMsg">New Reply</span>
                      )}
                    </div>
                  </>
                )}

                <button
                  className="homeHeader__dropdownItem"
                  onClick={() => navigate("/notifications")}
                >
                  Notifications
                </button>
                {userData && userData?.user_type === "wholeseller" && (
                  <button
                    className="homeHeader__dropdownItem"
                    onClick={() => {
                      navigate("/bank-details");
                    }}
                  >
                    Bank Details
                  </button>
                )}
                <button
                  className="homeHeader__dropdownItem"
                  onClick={() => {
                    window.open("/contact-us", "_blank");
                  }}
                >
                  Contact Us
                </button>
                {userData ? (
                  <button
                    className="homeHeader__dropdownItem homeHeader__logout"
                    onClick={onLogout}
                  >
                    Logout
                  </button>
                ) : (
                  <button
                    className="homeHeader__dropdownItem"
                    onClick={() => navigate("/login")}
                  >
                    Login
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer
          isOpen={isDrawerOpen}
          onClose={closeDrawer}
          categories={categories}
          userData={userData}
          isCategoriesDropdownOpen={isCategoriesDropdownOpen}
          isAccountDropdownOpen={isAccountDropdownOpen}
          toggleCategoriesDropdown={toggleCategoriesDropdown}
          toggleAccountDropdown={toggleAccountDropdown}
          handleNavigation={handleNavigation}
          onLogout={onLogout}
          openCartDrawer={openCartDrawer}
          newMessageFlag={newMessageFlag}
          hideSearch={hideHeaderSearch}
        />
      )}

      {/* Cart Drawer */}
      <CartDrawer
        isOpen={isCartDrawerOpen}
        onClose={closeCartDrawer}
      />
    </header>
  );
};

export default HomeHeader;
